<template>
  <div class="kline-page">
    <!-- 左侧导航栏 (加宽以容纳更多数据) -->
    <div class="left-sidebar">
      <div class="stock-header">
        <div class="stock-code">{{ stockInfo.code || '688347' }}</div>
        <div class="stock-name">{{ stockInfo.name || '华虹公司' }}</div>
      </div>

      <!-- 实时行情 - 紧凑版 -->
      <div class="quote-panel">
        <div class="quote-row" v-if="latestQuote">
          <span class="label">最新</span>
          <span :class="['value', quoteClass]">{{ latestQuote.close || '-' }}</span>
          <span :class="['pct', quoteClass]">{{ (latestQuote.pct_chg || 0).toFixed(2) }}%</span>
        </div>
        <div class="quote-row" v-if="detailQuote">
          <span class="label">开盘</span>
          <span class="value">{{ detailQuote.open || '-' }}</span>
          <span class="sub-label">最高</span>
          <span class="value text-up">{{ detailQuote.high || '-' }}</span>
        </div>
        <div class="quote-row" v-if="detailQuote">
          <span class="label">最低</span>
          <span class="value text-down">{{ detailQuote.low || '-' }}</span>
          <span class="sub-label">量比</span>
          <span class="value">{{ bidask.intraday_volume_ratio || bidask.volume_ratio || '--' }}</span>
        </div>
        <div class="quote-row" v-if="detailQuote">
          <span class="label">成交量</span>
          <span class="value">{{ formatVol(detailQuote.volume) }}</span>
        </div>
        <div class="quote-row" v-if="detailQuote">
          <span class="label">成交额</span>
          <span class="value">{{ formatAmount(detailQuote.amount) }}</span>
        </div>
        <div class="quote-row" v-if="detailQuote">
          <span class="label">换手率</span>
          <span class="value">{{ (detailQuote.turnover_rate || 0).toFixed(2) }}%</span>
          <span class="sub-label">振幅</span>
          <span class="value">{{ (detailQuote.amplitude || 0).toFixed(2) }}%</span>
        </div>
        <div class="quote-divider"></div>
        <!-- 基本面 (来源: daily_basic + 东方财富) -->
        <div class="quote-row" v-if="bidask.pe_dynamic">
          <span class="label">市盈率</span>
          <span class="value">{{ bidask.pe_dynamic || '--' }}</span>
          <span class="sub-label">市净率</span>
          <span class="value">{{ fundamentals.pb || '--' }}</span>
        </div>
        <div class="quote-row" v-if="bidask.total_mv_yi">
          <span class="label">总市值</span>
          <span class="value">{{ bidask.total_mv_yi ? bidask.total_mv_yi + '亿' : '--' }}</span>
          <span class="sub-label">市销率</span>
          <span class="value">{{ fundamentals.ps_ttm || fundamentals.ps || '--' }}</span>
        </div>
        <div class="quote-row" v-if="bidask.circ_mv_yi">
          <span class="label">流通市值</span>
          <span class="value">{{ bidask.circ_mv_yi ? bidask.circ_mv_yi + '亿' : '--' }}</span>
          <span class="sub-label">股息率</span>
          <span class="value">{{ fundamentals.dv_ratio ? fundamentals.dv_ratio + '%' : '--' }}</span>
        </div>
        <div class="quote-row" v-if="fundamentals.total_share">
          <span class="label">总股本</span>
          <span class="value">{{ (fundamentals.total_share / 10000).toFixed(2) + '亿' }}</span>
          <span class="sub-label">流通股本</span>
          <span class="value">{{ fundamentals.float_share ? (fundamentals.float_share / 10000).toFixed(2) + '亿' : '--' }}</span>
        </div>
      </div>

      <!-- 周期切换 - 完整多周期(1分~月线) -->
      <div class="period-panel">
        <div class="panel-title">周期</div>
        <div class="period-btns">
          <!-- 分钟级 -->
          <el-button :type="activePeriod === '1min' ? 'primary' : 'info'" size="mini" @click="changePeriod('1min')">1分</el-button>
          <el-button :type="activePeriod === '5min' ? 'primary' : 'info'" size="mini" @click="changePeriod('5min')">5分</el-button>
          <el-button :type="activePeriod === '15min' ? 'primary' : 'info'" size="mini" @click="changePeriod('15min')">15分</el-button>
          <el-button :type="activePeriod === '30min' ? 'primary' : 'info'" size="mini" @click="changePeriod('30min')">30分</el-button>
          <el-button :type="activePeriod === '60min' ? 'primary' : 'info'" size="mini" @click="changePeriod('60min')">60分</el-button>
          <!-- 日级别 -->
          <el-button :type="activePeriod === 'daily' ? 'primary' : 'warning'" size="mini" @click="changePeriod('daily')">日线</el-button>
          <el-button :type="activePeriod === 'weekly' ? 'primary' : 'info'" size="mini" @click="changePeriod('weekly')">周线</el-button>
          <el-button :type="activePeriod === 'monthly' ? 'primary' : 'info'" size="mini" @click="changePeriod('monthly')">月线</el-button>
        </div>
      </div>

      <!-- 主图指标切换 -->
      <div class="indicator-panel">
        <div class="panel-title">主图指标</div>
        <div class="indicator-toggle">
          <el-checkbox v-model="showMA5">MA5</el-checkbox>
          <el-checkbox v-model="showMA10">MA10</el-checkbox>
          <el-checkbox v-model="showMA20">MA20</el-checkbox>
          <el-checkbox v-model="showMA60">MA60</el-checkbox>
        </div>
      </div>

      <!-- 副图指标切换 -->
      <div class="sub-indicator-panel">
        <div class="panel-title">副图指标</div>
        <div class="sub-indicator-toggle">
          <el-checkbox v-model="showVOL">VOL</el-checkbox>
          <el-checkbox v-model="showMACD">MACD</el-checkbox>
          <el-checkbox v-model="showKDJ">KDJ</el-checkbox>
          <el-checkbox v-model="showSAR">SAR</el-checkbox>
          <el-checkbox v-model="showRSI">RSI</el-checkbox>
        </div>
      </div>

      <!-- 股票代码输入 -->
      <div class="stock-input-panel">
        <div class="panel-title">切换股票</div>
        <StockCodeInput v-model="currentTsCode" placeholder="输入股票代码" width="100%" @select="onStockSelect" />
      </div>
    </div>

    <!-- 右侧图表区域 -->
    <div class="chart-area">
      <div v-if="loading" class="loading-mask">
        <i class="el-icon-loading"></i> 加载K线数据中...
      </div>

      <!-- K线主图 -->
      <div class="chart-main" ref="mainChart"></div>

      <!-- 成交量副图 -->
      <div class="chart-vol" ref="volChart" v-if="showVOL && klineData.length > 0"></div>

      <!-- MACD副图 -->
      <div class="chart-macd" ref="macdChart" v-if="showMACD && klineData.length > 0"></div>

      <!-- KDJ副图 -->
      <div class="chart-kdj" ref="kdjChart" v-if="showKDJ && klineData.length > 0"></div>

      <!-- SAR副图 (在主图中叠加显示) -->

      <!-- RSI副图 -->
      <div class="chart-rsi" ref="rsiChart" v-if="showRSI && klineData.length > 0"></div>

      <div v-if="!loading && klineData.length === 0" class="empty-hint">
        暂无数据，请检查股票代码或稍后重试
      </div>
    </div>

    <!-- 右侧数据面板：逐笔/竞价/分钟/因子/资金 -->
    <div class="right-sidebar">
      <el-tabs v-model="rightTab" type="card" size="mini" class="right-tabs">
        <!-- 逐笔成交 -->
        <el-tab-pane label="逐笔" name="ticks">
          <div class="right-panel-body">
            <div class="right-empty" v-if="!ticks || !ticks.length">暂无逐笔成交数据</div>
            <template v-else>
              <div class="tick-summary-bar" v-if="tickStats.avgPrice">均价 {{ tickStats.avgPrice }} | 总量 {{ formatVol(tickStats.totalVol) }}</div>
              <div class="ticks-list">
                <div class="tick-item" v-for="(t, i) in ticksDisplay" :key="i">
                  <span class="tick-time">{{ t.time }}</span>
                  <span :class="['tick-price', t.price >= tickBase ? 'text-up' : 'text-down']">{{ t.price }}</span>
                  <span class="tick-vol">{{ t.volume }}</span>
                  <span :class="['tick-arrow', t.price >= tickBase ? 'up-arrow' : 'down-arrow']">{{ t.price >= tickBase ? '▲' : '▼' }}</span>
                </div>
                <div class="tick-more" v-if="ticks.length > 15">... 共{{ ticks.length }}笔</div>
              </div>
            </template>
          </div>
        </el-tab-pane>

        <!-- 集合竞价 -->
        <el-tab-pane label="竞价" name="auction">
          <div class="right-panel-body">
            <span class="auction-date" v-if="auctionDate">({{ auctionDate }})</span>
            <div class="right-empty" v-if="!auctionOpen && !auctionClose">暂无集合竞价数据</div>
            <div class="auction-section" v-if="auctionOpen">
              <div class="auction-label">开盘 9:30</div>
              <table class="auction-table">
                <tr><td class="a-name">开盘</td><td class="a-val text-up">{{ auctionOpen.open }}</td><td class="a-name">收盘</td><td class="a-val">{{ auctionOpen.close }}</td></tr>
                <tr><td class="a-name">最高</td><td class="a-val text-up">{{ auctionOpen.high }}</td><td class="a-name">最低</td><td class="a-val text-down">{{ auctionOpen.low }}</td></tr>
                <tr><td class="a-name">成交量</td><td class="a-val">{{ formatVol(auctionOpen.vol) }}</td><td class="a-name">成交额</td><td class="a-val">{{ formatAmount(auctionOpen.amount) }}</td></tr>
                <tr><td class="a-name">均价</td><td class="a-val">{{ auctionOpen.vwap }}</td><td colspan="2"></td></tr>
              </table>
            </div>
            <div class="auction-section" v-if="auctionClose">
              <div class="auction-label">收盘 15:00</div>
              <table class="auction-table">
                <tr><td class="a-name">开盘</td><td class="a-val text-up">{{ auctionClose.open }}</td><td class="a-name">收盘</td><td class="a-val">{{ auctionClose.close }}</td></tr>
                <tr><td class="a-name">最高</td><td class="a-val text-up">{{ auctionClose.high }}</td><td class="a-name">最低</td><td class="a-val text-down">{{ auctionClose.low }}</td></tr>
                <tr><td class="a-name">成交量</td><td class="a-val">{{ formatVol(auctionClose.vol) }}</td><td class="a-name">成交额</td><td class="a-val">{{ formatAmount(auctionClose.amount) }}</td></tr>
                <tr><td class="a-name">均价</td><td class="a-val">{{ auctionClose.vwap }}</td><td colspan="2"></td></tr>
              </table>
            </div>
          </div>
        </el-tab-pane>

        <!-- 历史分钟行情 -->
        <el-tab-pane label="分钟" name="minutes">
          <div class="right-panel-body">
            <div class="right-empty" v-if="!rtMinutes">暂无分钟行情数据</div>
            <div class="rt-minutes-list" v-else>
              <div class="min-bar" v-for="(m, i) in rtMinutes.slice(-40)" :key="i">
                <span class="min-time">{{ formatMinTime(m.time) }}</span>
                <span class="min-dir" :class="m.close >= m.open ? 'text-up' : 'text-down'">O{{ m.open }}</span>
                <span class="min-price" :class="m.close >= m.open ? 'text-up' : 'text-down'">C{{ m.close }}</span>
                <span class="min-vol">{{ formatVol(m.vol) }}</span>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- 技术面因子 -->
        <el-tab-pane label="因子" name="factor">
          <div class="right-panel-body">
            <div class="right-empty" v-if="!factorPro">暂无技术面数据</div>
            <div class="factor-grid" v-else>
              <div class="fg-item" style="border-bottom:1px solid rgba(255,255,255,0.06);margin-bottom:4px;padding-bottom:4px;">
                <span class="fg-label" style="color:#409EFF;">{{ activePeriodLabel }}</span>
                <span class="fg-val" style="color:#aaa;">收盘 {{ p2(factorPro.close) }}</span>
              </div>
              <div class="fg-item"><span class="fg-label">MA5/10/20/60</span><span class="fg-val">{{ p2(factorPro.ma5) }} / {{ p2(factorPro.ma10) }} / {{ p2(factorPro.ma20) }} / {{ p2(factorPro.ma60) }}</span></div>
              <!-- MACD 数值 + BAR说明 + 状态标签 -->
              <div class="fg-item">
                <span class="fg-label">MACD</span>
                <span class="fg-val">
                  DIF <span style="color:#409EFF">{{ p4(factorPro.macd_dif) }}</span><span v-if="factorPro.macd_dif_prev != null" :style="{ color: factorPro.macd_dif >= factorPro.macd_dif_prev ? '#F56C6C' : '#67C23A' }">{{ factorPro.macd_dif >= factorPro.macd_dif_prev ? '↑' : '↓' }}</span>
                  DEA <span style="color:#E6A23C">{{ p4(factorPro.macd_dea) }}</span><span v-if="factorPro.macd_dea_prev != null" :style="{ color: factorPro.macd_dea >= factorPro.macd_dea_prev ? '#F56C6C' : '#67C23A' }">{{ factorPro.macd_dea >= factorPro.macd_dea_prev ? '↑' : '↓' }}</span>
                  BAR <span :style="{ color: factorPro.macd >= 0 ? '#F56C6C' : '#67C23A' }">{{ p4(factorPro.macd) }}</span><span v-if="factorPro.macd_prev != null" :style="{ color: factorPro.macd >= factorPro.macd_prev ? '#F56C6C' : '#67C23A' }">{{ factorPro.macd >= factorPro.macd_prev ? '↑' : '↓' }}</span>
                </span>
              </div>
              <div class="fg-item">
                <span class="fg-label">BAR说明</span>
                <span class="fg-val" style="color:#888;">BAR = 2 × (DIF − DEA)</span>
              </div>
              <div class="fg-item">
                <span class="fg-label">状态</span>
                <span class="fg-val">
                  <span :style="{ color: macdState.cross === '金叉' ? '#F56C6C' : '#67C23A' }">{{ macdState.cross }}</span>
                  <span :style="{ color: macdState.axis === '零轴上' ? '#F56C6C' : '#67C23A' }">{{ macdState.axis }}</span>
                  <span :style="{ color: macdState.barTrend === 'BAR放大' ? '#F56C6C' : '#67C23A' }">{{ macdState.barTrend }}</span>
                </span>
              </div>
              <div class="fg-item"><span class="fg-label">KDJ</span><span class="fg-val">K <span style="color:#333">{{ p2(factorPro.kdj_k) }}</span> D <span style="color:#d4a020">{{ p2(factorPro.kdj_d) }}</span> J <span style="color:#7e57c2">{{ p2(factorPro.kdj_j) }}</span></span></div>
              <div class="fg-item"><span class="fg-label">RSI(6/12/24)</span><span class="fg-val"><span :style="{ color: factorPro.rsi_6 >= 50 ? '#F56C6C' : '#67C23A' }">{{ p2(factorPro.rsi_6) }}</span> / <span :style="{ color: factorPro.rsi_12 >= 50 ? '#F56C6C' : '#67C23A' }">{{ p2(factorPro.rsi_12) }}</span> / <span :style="{ color: factorPro.rsi_24 >= 50 ? '#F56C6C' : '#67C23A' }">{{ p2(factorPro.rsi_24) }}</span></span></div>
              <div class="fg-item"><span class="fg-label">BOLL</span><span class="fg-val">上{{ p2(factorPro.boll_upper) }} 中{{ p2(factorPro.boll_mid) }} 下{{ p2(factorPro.boll_lower) }}</span></div>
              <div class="fg-item" v-if="factorPro.atr_14 != null"><span class="fg-label">ATR(14)</span><span class="fg-val">{{ p4(factorPro.atr_14) }}</span></div>
            </div>
          </div>
        </el-tab-pane>

        <!-- 资金流向 -->
        <el-tab-pane label="资金" name="fund">
          <div class="right-panel-body">
            <div class="right-empty" v-if="!fundFlow || fundFlow.error">暂无资金流向数据</div>
            <template v-else>
              <div class="fund-summary" v-if="fundFlow.net_amount != null">
                <div class="fund-row">
                  <span class="flabel">主力流入</span>
                  <span :class="['fval', fundFlow.net_amount >= 0 ? 'text-up' : 'text-down']">{{ formatFund(fundMainIn) }}</span>
                </div>
                <div class="fund-row">
                  <span class="flabel">主力流出</span>
                  <span class="fval text-down">{{ formatFund(Math.abs(fundMainOut)) }}</span>
                </div>
                <div class="fund-row highlight">
                  <span class="flabel">净流入</span>
                  <span :class="['fval bold', fundFlow.net_amount >= 0 ? 'text-up' : 'text-down']">{{ formatFund(fundFlow.net_amount) }}</span>
                </div>
              </div>
              <div class="fund-breakdown" v-if="fundFlow.buy_lg_amount != null">
                <div class="fb-item">
                  <span class="fb-name">特大/大单</span>
                  <div class="fb-bar-wrap">
                    <span class="fb-in" :style="{ width: fbLgInW }"></span>
                    <span class="fb-out" :style="{ width: fbLgOutW }"></span>
                  </div>
                  <span :class="['fb-net', (fundFlow.buy_lg_amount||0) >= 0 ? 'text-up' : 'text-down']">{{ fmtSigned(fundFlow.buy_lg_amount) }}</span>
                </div>
                <div class="fb-item">
                  <span class="fb-name">中单</span>
                  <div class="fb-bar-wrap">
                    <span class="fb-in" :style="{ width: fbMdInW }"></span>
                    <span class="fb-out" :style="{ width: fbMdOutW }"></span>
                  </div>
                  <span :class="['fb-net', (fundFlow.buy_md_amount||0) >= 0 ? 'text-up' : 'text-down']">{{ fmtSigned(fundFlow.buy_md_amount) }}</span>
                </div>
                <div class="fb-item">
                  <span class="fb-name">小单</span>
                  <div class="fb-bar-wrap">
                    <span class="fb-in" :style="{ width: fbSmInW }"></span>
                    <span class="fb-out" :style="{ width: fbSmOutW }"></span>
                  </div>
                  <span :class="['fb-net', (fundFlow.buy_sm_amount||0) >= 0 ? 'text-up' : 'text-down']">{{ fmtSigned(fundFlow.buy_sm_amount) }}</span>
                </div>
              </div>
            </template>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import StockCodeInput from '@/components/StockCodeInput.vue'
import { dataSourceApi } from '@/api'

export default {
  name: 'StockKline',
  components: { StockCodeInput },
  data() {
    return {
      currentTsCode: '688347.SH',
      stockInfo: { code: '688347', name: '华虹公司', industry: '半导体' },
      activePeriod: 'daily',
      rightTab: 'ticks',
      loading: false,
      klineData: [],
      latestQuote: null,
      realtimeQuote: null,  // 实时行情快照（市盈率/市值/量比等）

      // 综合实时详情数据
      detailQuote: null,   // 基础报价(从 /kline/detail)
      bidask: { asks: [], bids: [] },  // 买卖盘口
      ticks: [],            // 逐笔成交列表
      fundFlow: {},         // 资金流向
      fundamentals: {},     // 基本面(daily_basic: PB/PS/股息率/股本等)
      auctionOpen: null,    // 开盘集合竞价
      auctionClose: null,   // 收盘集合竞价
      auctionDate: '',      // 集合竞价日期
      rtMinutes: null,      // 历史分钟行情 (stk_mins/rt_min 1分钟线)
      factorPro: null,      // 技术面因子 (stk_factor_pro)

      // 主图均线开关
      showMA5: true,
      showMA10: true,
      showMA20: true,
      showMA60: false,

      // 副图指标开关
      showVOL: true,
      showMACD: true,
      showKDJ: true,
      showSAR: false,
      showRSI: true,

      // 图表实例
      mainChartInst: null,
      volChartInst: null,
      macdChartInst: null,
      kdjChartInst: null,
      rsiChartInst: null,

      // 颜色常量 - 专业看盘配色
      colors: {
        up: '#ee0a0a',         // 阳线红
        down: '#12b321',       // 阴线绿
        upLight: 'rgba(238, 10, 10, 0.8)',
        downLight: 'rgba(18, 179, 33, 0.8)',
        bg: '#1a1a2e',         // 深色背景
        bgSecondary: '#16213e', // 次级背景
        gridLine: '#2a2a4a',   // 网格线
        text: '#c0c0c0',       // 文字颜色
        textLight: '#888888',  // 浅色文字
        ma5: '#ef5350',        // MA5 红橙
        ma10: '#42a5f5',       // MA10 蓝
        ma20: '#ffee58',       // MA20 黄
        ma60: '#ab47bc',       // MA60 紫
        dea: '#26c6da',        // DEA 青
        dif: '#ff7043',        // DIF 橙
        macdBarUp: '#ee0a0a',
        macdBarDown: '#12b321',
        kj: '#e91e63',         // K
        dj: '#00bcd4',         // D,J
        sarUp: '#ff5722',
        sarDown: '#4caf50',
        rsi1: '#ff9800',
        rsi2: '#2196f3',
        rsi3: '#9c27b0',
        volMa5: '#ffe082',
        volMa10: '#80deea',
        volMa20: '#ce93d8'
      }
    }
  },
  computed: {
    quoteClass() {
      if (!this.latestQuote) return ''
      const pct = this.latestQuote.pct_chg || 0
      return pct >= 0 ? 'text-up' : 'text-down'
    },
    activePeriodLabel() {
      const map = { '1min':'1分钟', '5min':'5分钟', '15min':'15分钟', '30min':'30分钟', '60min':'60分钟', 'daily':'日线', 'weekly':'周线', 'monthly':'月线' }
      return map[this.activePeriod] || this.activePeriod
    },
    // MACD 状态标签计算（金叉/死叉、零轴上/下、BAR放大/缩短）
    macdState() {
      const f = this.factorPro
      if (!f) return { cross: '--', axis: '--', barTrend: '--' }
      const dif = Number(f.macd_dif)
      const dea = Number(f.macd_dea)
      const bar = Number(f.macd)
      const prevDif = f.macd_dif_prev != null ? Number(f.macd_dif_prev) : null
      const prevDea = f.macd_dea_prev != null ? Number(f.macd_dea_prev) : null
      const prevBar = f.macd_prev != null ? Number(f.macd_prev) : null

      // 金叉/死叉：DIF 上穿/下穿 DEA
      let cross = '--'
      if (prevDif != null && prevDea != null) {
        if (prevDif <= prevDea && dif > dea) cross = '金叉'
        else if (prevDif >= prevDea && dif < dea) cross = '死叉'
      }

      // 零轴上/下
      const axis = dif >= 0 ? '零轴上' : '零轴下'

      // BAR放大/缩短：|BAR| 变大/变小
      let barTrend = '--'
      if (prevBar != null) {
        const currAbs = Math.abs(bar)
        const prevAbs = Math.abs(prevBar)
        barTrend = currAbs >= prevAbs ? 'BAR放大' : 'BAR缩短'
      }

      return { cross, axis, barTrend }
    },
    // 逐笔成交显示(最多15条)
    ticksDisplay() { return (this.ticks || []).slice(0, 15) },
    tickBase() {
      if (!this.ticks || !this.ticks.length) return 0
      return this.ticks[0].price || 0
    },
    tickStats() {
      if (!this.ticks || !this.ticks.length) return { avgPrice: null, totalVol: 0 }
      const total = this.ticks.reduce((s, t) => s + (t.volume || 0), 0)
      const sumPrice = this.ticks.reduce((s, t) => s + (t.price || 0) * (t.volume || 0), 0)
      return { avgPrice: total > 0 ? (sumPrice / total).toFixed(2) : '--', totalVol: total }
    },
    // 资金流向计算辅助
    fundMainIn() {
      if (this.fundFlow.net_amount == null) return 0
      const net = this.fundFlow.net_amount || 0
      return Math.abs(net) + Math.max(0, net) * 2
    },
    fundMainOut() {
      if (this.fundFlow.net_amount == null) return 0
      const net = this.fundFlow.net_amount || 0
      return Math.abs(net) + Math.max(0, -net) * 2
    },
    fbLgInW() { return this._fbBarW('buy_lg_amount', true) },
    fbLgOutW() { return this._fbBarW('buy_lg_amount', false) },
    fbMdInW() { return this._fbBarW('buy_md_amount', true) },
    fbMdOutW() { return this._fbBarW('buy_md_amount', false) },
    fbSmInW() { return this._fbBarW('buy_sm_amount', true) },
    fbSmOutW() { return this._fbBarW('buy_sm_amount', false) },
  },
  mounted() {
    this.loadData()
    this.fetchRealtimeQuote()
    this.fetchRealtimeDetail()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.disposeAllCharts()
    window.removeEventListener('resize', this.handleResize)
  },
  watch: {
    showMA5() { this.renderMainChart() },
    showMA10() { this.renderMainChart() },
    showMA20() { this.renderMainChart() },
    showMA60() { this.renderMainChart() },
    showVOL(val) {
      if (val) { this.$nextTick(this.renderVolChart) } else { this.disposeChart('vol') }
    },
    showMACD(val) {
      if (val) { this.$nextTick(this.renderMacdChart) } else { this.disposeChart('macd') }
    },
    showKDJ(val) {
      if (val) { this.$nextTick(this.renderKdjChart) } else { this.disposeChart('kdj') }
    },
    showSAR() { this.renderMainChart() },
    showRSI(val) {
      if (val) { this.$nextTick(this.renderRsiChart) } else { this.disposeChart('rsi') }
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        // 根据周期选择合适的默认时间范围
        const rangeMap = { daily: '6m', weekly: '1y', monthly: '2y' }
        const range = rangeMap[this.activePeriod] || '6m'
        
        const res = await dataSourceApi.getKlineData(this.currentTsCode, {
          freq: this.activePeriod,
          period: range
        })
        // 增强版：分钟级使用增强API
        let data = []
        if (['1min','5min','15min','30min','60min'].includes(this.activePeriod)) {
          try {
            const apiRes = await dataSourceApi.getKlineDataV2(this.currentTsCode, {
              period: this.activePeriod, count: 500, source: 'auto'
            })
            if (apiRes && apiRes.data && Array.isArray(apiRes.data)) {
              data = apiRes.data.map(d => ({
                ...d, trade_date: d.time || d.trade_date,
                open: d.open != null ? d.open : 0, high: d.high != null ? d.high : 0, low: d.low != null ? d.low : 0,
                close: d.close != null ? d.close : 0, vol: d.volume || d.vol || 0
              }))
              if (apiRes.latest) this.latestQuote = apiRes.latest
              // 使用后端返回的技术指标更新右侧因子面板
              if (apiRes.indicators) {
                this.factorPro = apiRes.indicators
              }
            }
          } catch (e2) { console.warn('增强API回退:', e2) }
        }
        if (!data.length) { data = Array.isArray(res) ? res : [] }

        // 按日期升序排列
        this.klineData = data.sort((a, b) => {
          const aVal = a.trade_date || a.time || ''
          const bVal = b.trade_date || b.time || ''
          return aVal.localeCompare(bVal)
        })
        if (this.klineData.length > 0 && !this.latestQuote) {
          this.latestQuote = this.klineData[this.klineData.length - 1]
        }
        this.$nextTick(() => {
          this.initAllCharts()
        })
      } catch (e) {
        console.error('加载K线数据失败:', e)
        this.$message.error('加载K线数据失败')
      } finally {
        this.loading = false
      }
    },

    initAllCharts() {
      this.renderMainChart()
      if (this.showVOL) this.renderVolChart()
      if (this.showMACD) this.renderMacdChart()
      if (this.showKDJ) this.renderKdjChart()
      if (this.showRSI) this.renderRsiChart()
    },

    disposeAllCharts() {
      this.disposeChart('main')
      this.disposeChart('vol')
      this.disposeChart('macd')
      this.disposeChart('kdj')
      this.disposeChart('rsi')
    },

    disposeChart(name) {
      const instMap = {
        main: 'mainChartInst',
        vol: 'volChartInst',
        macd: 'macdChartInst',
        kdj: 'kdjChartInst',
        rsi: 'rsiChartInst'
      }
      const refMap = {
        main: 'mainChart',
        vol: 'volChart',
        macd: 'macdChart',
        kdj: 'kdjChart',
        rsi: 'rsiChart'
      }
      if (this[instMap[name]]) {
        this[instMap[name]].dispose()
        this[instMap[name]] = null
      }
    },

    handleResize() {
      const charts = ['mainChartInst', 'volChartInst', 'macdChartInst', 'kdjChartInst', 'rsiChartInst']
      charts.forEach(c => {
        if (this[c]) this[c].resize()
      })
    },

    changePeriod(p) {
      this.activePeriod = p
      this.disposeAllCharts()
      this.loadData()
    },

    onStockSelect(item) {
      if (item && item.ts_code) {
        this.currentTsCode = item.ts_code
        this.stockInfo = { code: item.code6, name: item.name, industry: item.industry }
        this.disposeAllCharts()
        this.loadData()
        this.fetchRealtimeQuote()
        this.fetchRealtimeDetail()
      }
    },

    formatVol(vol) {
      if (!vol) return '-'
      if (vol >= 100000000) return (vol / 100000000).toFixed(2) + '亿'
      if (vol >= 10000) return (vol / 10000).toFixed(0) + '万'
      return vol.toFixed(0)
    },

    formatAmount(amount) {
      if (!amount) return '-'
      if (amount >= 100000000) return (amount / 100000000).toFixed(2) + '亿'
      if (amount >= 10000) return (amount / 10000).toFixed(0) + '万'
      return amount.toFixed(0)
    },

    // ==================== 计算技术指标 ====================

    calcMA(data, dayCount) {
      const result = []
      for (let i = 0; i < data.length; i++) {
        if (i < dayCount - 1) {
          result.push(null)
          continue
        }
        let sum = 0
        for (let j = 0; j < dayCount; j++) {
          sum += data[i - j].close
        }
        result.push(parseFloat((sum / dayCount).toFixed(3)))
      }
      return result
    },

    calcVOL_MA(volData, dayCount) {
      const result = []
      for (let i = 0; i < volData.length; i++) {
        if (i < dayCount - 1) {
          result.push(null)
          continue
        }
        let sum = 0
        for (let j = 0; j < dayCount; j++) {
          sum += volData[i - j]
        }
        result.push(parseFloat((sum / dayCount).toFixed(0)))
      }
      return result
    },

    calcMACD(data, short = 12, long = 26, mid = 9) {
      const emaShort = [], emaLong = [], dif = [], dea = [], macd = []
      for (let i = 0; i < data.length; i++) {
        const close = data[i].close
        if (i === 0) {
          emaShort.push(close); emaLong.push(close)
        } else {
          emaShort.push(close * 2 / (short + 1) + emaShort[i - 1] * (short - 1) / (short + 1))
          emaLong.push(close * 2 / (long + 1) + emaLong[i - 1] * (long - 1) / (long + 1))
        }
        const d = emaShort[i] - emaLong[i]
        dif.push(d)
        if (i === 0) { dea.push(d) }
        else { dea.push(d * 2 / (mid + 1) + dea[i - 1] * (mid - 1) / (mid + 1)) }
        macd.push(parseFloat(((d - dea[i]) * 2).toFixed(3)))
      }
      return { dif, dea, macd }
    },

    calcKDJ(data, n = 9) {
      const k = [], d = [], j = []
      let lastK = 50, lastD = 50
      for (let i = 0; i < data.length; i++) {
        const start = Math.max(0, i - n + 1)
        let hn = -Infinity, ln = Infinity
        for (let s = start; s <= i; s++) {
          hn = Math.max(hn, data[s].high)
          ln = Math.min(ln, data[s].low)
        }
        const rsv = hn === ln ? 50 : ((data[i].close - ln) / (hn - ln)) * 100
        const curK = 2 / 3 * lastK + 1 / 3 * rsv
        const curD = 2 / 3 * lastD + 1 / 3 * curK
        const curJ = 3 * curK - 2 * curD
        k.push(parseFloat(curK.toFixed(2)))
        d.push(parseFloat(curD.toFixed(2)))
        j.push(parseFloat(curJ.toFixed(2)))
        lastK = curK; lastD = curD
      }
      return { k, d, j }
    },

    calcSAR(data, afStart = 0.02, afStep = 0.02, afMax = 0.2) {
      const sar = [], epArr = [], afArr = [], trendArr = []
      let sarVal, ep, af, trend

      if (data.length < 2) return { sar: data.map(() => null), ep: [], af: [] }

      // 初始化
      trend = data[0].close >= data[1].close ? 1 : -1 // 1=上涨, -1=下跌
      if (trend === 1) {
        sarVal = Math.min(data[0].low, data[1].low)
        ep = data[0].high
      } else {
        sarVal = Math.max(data[0].high, data[1].high)
        ep = data[0].low
      }
      af = afStart
      sar.push(null) // 第一天无值
      epArr.push(ep); afArr.push(af); trendArr.push(trend)

      for (let i = 1; i < data.length; i++) {
        // 更新SAR
        sarVal = sarVal + af * (ep - sarVal)

        // 检查反转
        let reversed = false
        if (trend === 1) {
          if (sarVal > data[i].low) {
            reversed = true
            trend = -1
            sarVal = ep // 新SAR为前期极值
            ep = data[i].low
            af = afStart
          }
        } else {
          if (sarVal < data[i].high) {
            reversed = true
            trend = 1
            sarVal = ep
            ep = data[i].high
            af = afStart
          }
        }

        // 更新极点和加速因子
        if (!reversed) {
          if (trend === 1 && data[i].high > ep) {
            ep = data[i].high
            af = Math.min(af + afStep, afMax)
          } else if (trend === -1 && data[i].low < ep) {
            ep = data[i].low
            af = Math.min(af + afStep, afMax)
          }
        }

        sar.push(parseFloat(sarVal.toFixed(2)))
        epArr.push(ep); afArr.push(af); trendArr.push(trend)
      }
      return { sar, trend: trendArr }
    },

    calcRSI(data, period = 14) {
      const rsi = []
      let avgGain = 0, avgLoss = 0

      for (let i = 0; i < data.length; i++) {
        if (i === 0) {
          rsi.push(50)
          continue
        }
        const change = data[i].close - data[i - 1].close
        const gain = change > 0 ? change : 0
        const loss = change < 0 ? -change : 0

        if (i < period) {
          avgGain += gain
          avgLoss += loss
          if (i === period - 1) {
            avgGain /= period
            avgLoss /= period
          }
          rsi.push(50)
        } else {
          avgGain = (avgGain * (period - 1) + gain) / period
          avgLoss = (avgLoss * (period - 1) + loss) / period
          const rs = avgLoss === 0 ? 999 : avgGain / avgLoss
          rsi.push(parseFloat((100 - 100 / (1 + rs)).toFixed(2)))
        }
      }
      return rsi
    },

    // ==================== 渲染图表 ====================

    renderMainChart() {
      if (!this.$refs.mainChart) return
      if (this.mainChartInst) this.mainChartInst.dispose()
      this.mainChartInst = echarts.init(this.$refs.mainChart)

      const data = this.klineData
      if (!data || data.length === 0) return

      const dates = data.map(d => this.formatDate(d.trade_date))
      const candleData = data.map(d => [d.open, d.close, d.low, d.high])
      const isUp = data.map(d => d.close >= d.open)

      const series = [{
        name: 'K线',
        type: 'candlestick',
        data: candleData,
        itemStyle: {
          color: this.colors.up,
          color0: this.colors.down,
          borderColor: this.colors.up,
          borderColor0: this.colors.down
        }
      }]

      // MA均线
      if (this.showMA5) {
        series.push({ name: 'MA5', type: 'line', data: this.calcMA(data, 5), smooth: true, symbol: 'none', lineStyle: { width: 1, color: this.colors.ma5 }, z: 10 })
      }
      if (this.showMA10) {
        series.push({ name: 'MA10', type: 'line', data: this.calcMA(data, 10), smooth: true, symbol: 'none', lineStyle: { width: 1, color: this.colors.ma10 }, z: 10 })
      }
      if (this.showMA20) {
        series.push({ name: 'MA20', type: 'line', data: this.calcMA(data, 20), smooth: true, symbol: 'none', lineStyle: { width: 1, color: this.colors.ma20 }, z: 10 })
      }
      if (this.showMA60) {
        series.push({ name: 'MA60', type: 'line', data: this.calcMA(data, 60), smooth: true, symbol: 'none', lineStyle: { width: 1, color: this.colors.ma60 }, z: 10 })
      }

      // SAR抛物线指标
      if (this.showSAR) {
        const sarResult = this.calcSAR(data)
        const sarUp = [], sarDown = []
        sarResult.sar.forEach((v, i) => {
          if (v === null) { sarUp.push(null); sarDown.push(null) }
          else if (sarResult.trend[i] === 1) { sarUp.push(v); sarDown.push(null) }
          else { sarUp.push(null); sarDown.push(v) }
        })
        series.push({
          name: 'SAR', type: 'scatter', data: sarUp,
          symbolSize: 6, itemStyle: { color: this.colors.sarUp }, z: 15, silent: true
        })
        series.push({
          name: 'SAR', type: 'scatter', data: sarDown,
          symbolSize: 6, itemStyle: { color: this.colors.sarDown }, z: 15, silent: true
        })
      }

      const legendData = ['K线']
      if (this.showMA5) legendData.push('MA5')
      if (this.showMA10) legendData.push('MA10')
      if (this.showMA20) legendData.push('MA20')
      if (this.showMA60) legendData.push('MA60')

      this.mainChartInst.setOption({
        backgroundColor: this.colors.bg,
        animation: false,
        tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, backgroundColor: 'rgba(30,30,50,0.95)', borderColor: '#333', textStyle: { color: '#ccc' }, confine: true },
        legend: { data: legendData, top: 0, textStyle: { color: this.colors.text, fontSize: 11 }, itemWidth: 16, itemHeight: 10, pageTextStyle: { color: this.colors.text } },
        grid: { left: '8%', right: '3%', top: 30, bottom: 25 },
        xAxis: { type: 'category', data: dates, axisLine: { lineStyle: { color: this.colors.gridLine } }, axisLabel: { color: this.colors.textLight, fontSize: 10 }, axisTick: { show: false }, splitLine: { show: false } },
        yAxis: { type: 'value', scale: true, splitLine: { lineStyle: { color: this.colors.gridLine, type: 'dashed' } }, axisLine: { show: false }, axisLabel: { color: this.colors.textLight, fontSize: 10 }, axisTick: { show: false } },
        dataZoom: [{ type: 'inside', start: 40, end: 100 }, { type: 'slider', height: 18, bottom: 2, borderColor: this.colors.gridLine, fillerColor: 'rgba(64,158,255,0.15)', handleStyle: { color: '#409EFF' }, textStyle: { color: this.colors.textLight, fontSize: 10 } }],
        series
      }, true)
    },

    renderVolChart() {
      if (!this.$refs.volChart) return
      if (this.volChartInst) this.volChartInst.dispose()
      this.volChartInst = echarts.init(this.$refs.volChart)

      const data = this.klineData
      const dates = data.map(d => this.formatDate(d.trade_date))
      const vols = data.map(d => d.vol || 0)
      const isUp = data.map(d => d.close >= d.open)

      const series = [{
        name: '成交量',
        type: 'bar',
        data: vols.map((v, i) => ({
          value: v,
          itemStyle: { color: isUp[i] ? this.colors.upLight : this.colors.downLight }
        })),
        barWidth: '55%',
        z: 5
      }]
      if (this.showMA5) series.push({ name: 'VOLMA5', type: 'line', data: this.calcVOL_MA(vols, 5), symbol: 'none', lineStyle: { width: 1, color: this.colors.volMa5 }, z: 10 })
      if (this.showMA10) series.push({ name: 'VOLMA10', type: 'line', data: this.calcVOL_MA(vols, 10), symbol: 'none', lineStyle: { width: 1, color: this.colors.volMa10 }, z: 10 })
      if (this.showMA20) series.push({ name: 'VOLMA20', type: 'line', data: this.calcVOL_MA(vols, 20), symbol: 'none', lineStyle: { width: 1, color: this.colors.volMa20 }, z: 10 })

      const legData = ['成交量']
      if (this.showMA5) legData.push('VOLMA5')
      if (this.showMA10) legData.push('VOLMA10')
      if (this.showMA20) legData.push('VOLMA20')

      this.volChartInst.setOption({
        backgroundColor: this.colors.bg,
        animation: false,
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: 'rgba(30,30,50,0.95)', borderColor: '#333', textStyle: { color: '#ccc' }, formatter: p => `${p[0].axisValue}<br/>${p.map(s => `${s.marker} ${s.seriesName}: ${typeof s.value === 'object' ? s.value.value : s.value}`).join('<br/>')}` },
        legend: { data: legData, top: 0, textStyle: { color: this.colors.text, fontSize: 10 }, itemWidth: 14, itemHeight: 8 },
        grid: { left: '8%', right: '3%', top: 25, bottom: 22 },
        xAxis: { type: 'category', data: dates, axisLine: { lineStyle: { color: this.colors.gridLine } }, axisLabel: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
        yAxis: { type: 'value', scale: true, splitLine: { lineStyle: { color: this.colors.gridLine, type: 'dashed' } }, axisLine: { show: false }, axisLabel: { color: this.colors.textLight, fontSize: 9 }, axisTick: { show: false } },
        dataZoom: [{ type: 'inside', xAxisIndex: 0, start: 40, end: 100 }],
        series
      }, true)
    },

    renderMacdChart() {
      if (!this.$refs.macdChart) return
      if (this.macdChartInst) this.macdChartInst.dispose()
      this.macdChartInst = echarts.init(this.$refs.macdChart)

      const data = this.klineData
      const dates = data.map(d => this.formatDate(d.trade_date))
      const macd = this.calcMACD(data)

      this.macdChartInst.setOption({
        backgroundColor: this.colors.bg,
        animation: false,
        tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, backgroundColor: 'rgba(30,30,50,0.95)', borderColor: '#333', textStyle: { color: '#ccc' } },
        legend: { data: ['DIF', 'DEA', 'MACD'], top: 0, textStyle: { color: this.colors.text, fontSize: 10 }, itemWidth: 14, itemHeight: 8 },
        grid: { left: '8%', right: '3%', top: 25, bottom: 22 },
        xAxis: { type: 'category', data: dates, axisLine: { lineStyle: { color: this.colors.gridLine } }, axisLabel: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
        yAxis: { type: 'value', scale: true, splitNumber: 3, splitLine: { lineStyle: { color: this.colors.gridLine, type: 'dashed' } }, axisLine: { show: false }, axisLabel: { color: this.colors.textLight, fontSize: 9 }, axisTick: { show: false } },
        dataZoom: [{ type: 'inside', xAxisIndex: 0, start: 40, end: 100 }],
        series: [
          { name: 'DIF', type: 'line', data: macd.dif, symbol: 'none', lineStyle: { width: 1, color: this.colors.dif }, z: 10 },
          { name: 'DEA', type: 'line', data: macd.dea, symbol: 'none', lineStyle: { width: 1, color: this.colors.dea }, z: 10 },
          {
            name: 'MACD', type: 'bar', data: macd.macd.map(v => ({
              value: v,
              itemStyle: { color: v >= 0 ? this.colors.macdBarUp : this.colors.macdBarDown }
            })),
            barWidth: '45%', z: 5
          }
        ]
      }, true)
    },

    renderKdjChart() {
      if (!this.$refs.kdjChart) return
      if (this.kdjChartInst) this.kdjChartInst.dispose()
      this.kdjChartInst = echarts.init(this.$refs.kdjChart)

      const data = this.klineData
      const dates = data.map(d => this.formatDate(d.trade_date))
      const kdj = this.calcKDJ(data)

      this.kdjChartInst.setOption({
        backgroundColor: this.colors.bg,
        animation: false,
        tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, backgroundColor: 'rgba(30,30,50,0.95)', borderColor: '#333', textStyle: { color: '#ccc' } },
        legend: { data: ['K', 'D', 'J'], top: 0, textStyle: { color: this.colors.text, fontSize: 10 }, itemWidth: 14, itemHeight: 8 },
        grid: { left: '8%', right: '3%', top: 25, bottom: 22 },
        xAxis: { type: 'category', data: dates, axisLine: { lineStyle: { color: this.colors.gridLine } }, axisLabel: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
        yAxis: { type: 'value', scale: false, min: 0, max: 120, splitNumber: 3, splitLine: { lineStyle: { color: this.colors.gridLine, type: 'dashed' } }, axisLine: { show: false }, axisLabel: { color: this.colors.textLight, fontSize: 9 }, axisTick: { show: false } },
        dataZoom: [{ type: 'inside', xAxisIndex: 0, start: 40, end: 100 }],
        series: [
          { name: 'K', type: 'line', data: kdj.k, symbol: 'none', lineStyle: { width: 1, color: this.colors.kj }, z: 10 },
          { name: 'D', type: 'line', data: kdj.d, symbol: 'none', lineStyle: { width: 1, color: this.colors.dj }, z: 10 },
          { name: 'J', type: 'line', data: kdj.j, symbol: 'none', lineStyle: { width: 1, color: '#66bb6a' }, z: 10 }
        ]
      }, true)
    },

    renderRsiChart() {
      if (!this.$refs.rsiChart) return
      if (this.rsiChartInst) this.rsiChartInst.dispose()
      this.rsiChartInst = echarts.init(this.$refs.rsiChart)

      const data = this.klineData
      const dates = data.map(d => this.formatDate(d.trade_date))
      const rsi6 = this.calcRSI(data, 6)
      const rsi12 = this.calcRSI(data, 12)
      const rsi24 = this.calcRSI(data, 24)

      this.rsiChartInst.setOption({
        backgroundColor: this.colors.bg,
        animation: false,
        tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, backgroundColor: 'rgba(30,30,50,0.95)', borderColor: '#333', textStyle: { color: '#ccc' } },
        legend: { data: ['RSI6', 'RSI12', 'RSI24'], top: 0, textStyle: { color: this.colors.text, fontSize: 10 }, itemWidth: 14, itemHeight: 8 },
        grid: { left: '8%', right: '3%', top: 25, bottom: 22 },
        xAxis: { type: 'category', data: dates, axisLine: { lineStyle: { color: this.colors.gridLine } }, axisLabel: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
        yAxis: { type: 'value', scale: false, min: 0, max: 100, splitNumber: 3, splitLine: { lineStyle: { color: this.colors.gridLine, type: 'dashed' } }, axisLine: { show: false }, axisLabel: { color: this.colors.textLight, fontSize: 9 }, axisTick: { show: false } },
        dataZoom: [{ type: 'inside', xAxisIndex: 0, start: 40, end: 100 }],
        series: [
          { name: 'RSI6', type: 'line', data: rsi6, symbol: 'none', lineStyle: { width: 1, color: this.colors.rsi1 }, z: 10 },
          { name: 'RSI12', type: 'line', data: rsi12, symbol: 'none', lineStyle: { width: 1, color: this.colors.rsi2 }, z: 10 },
          { name: 'RSI24', type: 'line', data: rsi24, symbol: 'none', lineStyle: { width: 1, color: this.colors.rsi3 }, z: 10 }
        ],
        markLine: {
          silent: true,
          data: [
            { yAxis: 70, label: { formatter: '超买', color: '#f44336', fontSize: 9 }, lineStyle: { color: 'rgba(244,67,54,0.4)', type: 'dashed' } },
            { yAxis: 30, label: { formatter: '超卖', color: '#4caf50', fontSize: 9 }, lineStyle: { color: 'rgba(76,175,80,0.4)', type: 'dashed' } }
          ],
          symbol: 'none'
        }
      }, true)
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const s = String(dateStr)
      if (s.includes('-') && s.length >= 10) return s.substring(0, 10)  // 已有分隔符
      if (s.length >= 8) return s.substring(0, 4) + '-' + s.substring(4, 6) + '-' + s.substring(6, 8)
      return s
    },
    
    formatMarketCap(mv) {
      if (!mv || mv <= 0) return '--'
      const yi = mv / 100000000  // 元→亿元
      if (yi >= 10000) return (yi / 10000).toFixed(2) + '万亿'
      return yi.toFixed(0) + '亿'
    },

    async fetchRealtimeQuote() {
      try {
        const res = await dataSourceApi.getRealtimeQuote(this.currentTsCode)
        if (res && res.success) {
          this.realtimeQuote = {
            pe_ttm: res.pe_ttm,
            total_mv: res.total_mv || 0,  // 总市值(元)
            volume_ratio: res.volume_ratio || '--'
          }
          // 用实时数据更新 latestQuote 中的换手率等
          if (this.latestQuote) {
            this.latestQuote = {
              ...this.latestQuote,
              turnover_rate: res.turnover_rate || this.latestQuote.turnover_rate || 0
            }
          }
        }
      } catch (e) {
        // 静默失败
      }
    },

    async fetchRealtimeDetail() {
      try {
        const res = await dataSourceApi.getRealtimeDetail(this.currentTsCode)
        if (res && res.success) {
          // 基础报价
          if (res.quote) {
            this.detailQuote = res.quote
            // 同步更新 latestQuote
            this.latestQuote = {
              close: res.quote.price || res.quote.close,
              open: res.quote.open,
              high: res.quote.high,
              low: res.quote.low,
              change: res.quote.change,
              pct_chg: res.quote.change_pct,
              vol: res.quote.volume,
              amount: res.quote.amount,
              turnover_rate: res.quote.turnover_rate,
            }
          }
          // 买卖盘口
          if (res.bidask) {
            this.bidask = res.bidask
          }
          // 逐笔成交
          if (res.ticks) {
            this.ticks = res.ticks
          }
          // 资金流向
          if (res.fund_flow && !res.fund_flow.error) {
            this.fundFlow = res.fund_flow
          } else if (res.fund_flow) {
            this.fundFlow = {}
          }
          // 基本面 (daily_basic)
          if (res.fundamentals) {
            this.fundamentals = res.fundamentals
          }
          // 集合竞价（从 detail 统一返回）
          this.auctionOpen = res.open_auction || null
          this.auctionClose = res.close_auction || null
          this.auctionDate = res.auction_date || ''
          // 历史分钟行情（从 detail 统一返回）
          this.rtMinutes = (res.rt_minutes && res.rt_minutes.length) ? res.rt_minutes : null
          // 技术面因子（从 detail 统一返回）
          // 仅日线/周线/月线使用 stk_factor_pro；分钟级保留 loadData() 自算的分钟级MACD
          if (['daily', 'weekly', 'monthly'].includes(this.activePeriod)) {
            this.factorPro = res.factor_pro || null
          }
        }
      } catch (e) {
        console.warn('综合实时详情获取失败:', e)
      }
    },

    formatFund(val) {
      if (val == null) return '--'
      const abs = Math.abs(val)
      if (abs >= 10000) return (val / 10000).toFixed(2) + '亿'
      return val.toFixed(0) + '万'
    },

    fmtSigned(val) {
      if (val == null) return '--'
      const prefix = val >= 0 ? '+' : ''
      const abs = Math.abs(val)
      if (abs >= 10000) return prefix + (val / 10000).toFixed(2) + '亿'
      return prefix + val.toFixed(0) + '万'
    },

    _fbBarW(field, isIn) {
      const val = Math.abs(this.fundFlow[field] || 0)
      const lg = Math.max(
        Math.abs(this.fundFlow.buy_lg_amount || 0),
        Math.abs(this.fundFlow.buy_md_amount || 0),
        Math.abs(this.fundFlow.buy_sm_amount || 0),
        1
      )
      const pct = (val / lg) * 50
      if (isIn) {
        const net = this.fundFlow[field] || 0
        return net >= 0 ? pct + '%' : '0%'
      }
      const net = this.fundFlow[field] || 0
      return net < 0 ? pct + '%' : '0%'
    },

    // --- 历史分钟行情 / 技术面因子 辅助方法 ---
    formatMinTime(t) {
      if (!t) return ''
      // t like '20260529093000' or '09:30:00'
      if (t.includes(':')) return t.slice(0, 5)
      if (t.length >= 14) return t.slice(8, 10) + ':' + t.slice(10, 12)
      return t
    },
    formatNum(v) {
      if (v == null) return '--'
      const n = Number(v)
      if (Math.abs(n) >= 1e8) return (n / 1e8).toFixed(2) + '亿'
      if (Math.abs(n) >= 1e4) return (n / 1e4).toFixed(2) + '万'
      return n.toFixed(0)
    },
    p2(v) {
      if (v == null || isNaN(v)) return '--'
      return Number(v).toFixed(2)
    },
    p4(v) {
      if (v == null || isNaN(v)) return '--'
      return Number(v).toFixed(4)
    },
  }
}
</script>

<style scoped>
.kline-page {
  display: flex;
  height: calc(100vh - 110px);
  background: #1a1a2e;
  overflow: hidden;
}

/* ===== 左侧导航栏 (加宽) ===== */
.left-sidebar {
  width: 280px;
  min-width: 280px;
  background: #16213e;
  border-right: 1px solid #2a2a4a;
  padding: 10px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.left-sidebar::-webkit-scrollbar { width: 4px; }
.left-sidebar::-webkit-scrollbar-thumb { background: #444; border-radius: 2px; }

.stock-header {
  text-align: center;
  padding: 8px 0;
  border-bottom: 1px solid #2a2a4a;
}
.stock-code {
  font-size: 20px;
  font-weight: bold;
  color: #fff;
  font-family: monospace;
}
.stock-name {
  font-size: 12px;
  color: #909399;
  margin-top: 3px;
}

/* 行情面板 */
.quote-panel {
  background: #1a1a2e;
  border-radius: 6px;
  padding: 6px 8px;
}
.quote-row {
  display: flex;
  align-items: center;
  padding: 3px 0;
  font-size: 11.5px;
  border-bottom: 1px dashed rgba(255,255,255,0.05);
}
.quote-row:last-child { border-bottom: none; }
.quote-divider { border-top: 1px solid #333355; margin: 5px 0; }
.quote-row .label { color: #666; width: 48px; flex-shrink: 0; }
.quote-row .sub-label { color: #555; width: 36px; flex-shrink: 0; margin-left: 2px; font-size: 10px; }
.quote-row .value { color: #c0c0c0; font-family: monospace; font-weight: 500; flex: 1; text-align: right; }
.quote-row .pct { font-family: monospace; font-weight: 700; margin-left: 4px; min-width: 50px; text-align: right; }
.text-up { color: #ee0a0a !important; }
.text-down { color: #12b321 !important; }

/* 逐笔成交 */
.ticks-panel {
  background: #1a1a2e;
  border-radius: 6px;
  padding: 6px 6px;
}
.tick-summary { float: right; color: #555; font-size: 9px; font-weight: normal; }
.ticks-list { max-height: 180px; overflow-y: auto; }
.ticks-list::-webkit-scrollbar { width: 3px; }
.ticks-list::-webkit-scrollbar-thumb { background: #444; border-radius: 2px; }
.tick-item {
  display: flex;
  align-items: center;
  padding: 1.5px 2px;
  font-size: 10.5px;
  font-family: monospace;
  border-bottom: 1px solid rgba(255,255,255,0.03);
}
.tick-time { color: #666; width: 52px; flex-shrink: 0; }
.tick-price { width: 46px; text-align: right; font-weight: 600; flex-shrink: 0; }
.tick-vol { color: #888; width: 38px; text-align: right; flex-shrink: 0; }
.tick-arrow { width: 18px; text-align: center; font-size: 8px; flex-shrink: 0; }
.up-arrow { color: #ee0a0a; }
.down-arrow { color: #12b321; }
.tick-more { color: #555; text-align: center; font-size: 10px; padding: 3px 0; }

/* 集合竞价 */
.auction-panel {
  background: #1a1a2e;
  border-radius: 6px;
  padding: 6px 8px;
}
.auction-date { float: right; color: #888; font-size: 9px; font-weight: normal; }
.auction-empty {
  padding: 12px 0;
  text-align: center;
  color: #666;
  font-size: 11px;
}
.auction-section {
  margin-top: 4px;
  padding: 4px 0;
  border-top: 1px dashed rgba(255,255,255,0.05);
}
.auction-label {
  font-size: 10.5px;
  color: #409EFF;
  font-weight: 600;
  margin-bottom: 3px;
}
.auction-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10.5px;
  font-family: monospace;
}
.auction-table td { padding: 1.5px 2px; }
.auction-table .a-name { color: #666; width: 42px; text-align: right; padding-right: 6px; }
.auction-table .a-val { color: #c0c0c0; font-weight: 600; text-align: left; width: 56px; }

/* 历史分钟行情 */
.rt-minutes-panel {
  background: #1a1a2e;
  border-radius: 6px;
  padding: 6px 8px;
  margin-bottom: 6px;
}
.rt-minutes-empty {
  padding: 8px 0;
  text-align: center;
  color: #666;
  font-size: 11px;
}
.rt-minutes-list {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
}
.rt-minutes-list::-webkit-scrollbar { width: 3px; }
.rt-minutes-list::-webkit-scrollbar-thumb { background: #333; border-radius: 2px; }
.min-bar {
  display: flex;
  align-items: center;
  padding: 2px 4px;
  gap: 10px;
  font-size: 10.5px;
  font-family: monospace;
  border-bottom: 1px solid rgba(255,255,255,0.03);
}
.min-bar:hover { background: rgba(255,255,255,0.03); }
.min-time { color: #666; width: 42px; flex-shrink: 0; }
.min-dir { width: 64px; flex-shrink: 0; }
.min-price { width: 64px; flex-shrink: 0; }
.min-vol { color: #888; margin-left: auto; }

/* 技术面因子 */
.factor-panel {
  background: #1a1a2e;
  border-radius: 6px;
  padding: 6px 8px;
  margin-bottom: 6px;
}
.factor-empty {
  padding: 8px 0;
  text-align: center;
  color: #666;
  font-size: 11px;
}
.factor-grid {
  margin-top: 4px;
}
.fg-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 4px;
  font-size: 10.5px;
  font-family: monospace;
  border-bottom: 1px solid rgba(255,255,255,0.03);
}
.fg-label { color: #666; flex-shrink: 0; }
.fg-val { color: #c0c0c0; text-align: right; }

/* 资金流向 */
.fund-panel {
  background: #1a1a2e;
  border-radius: 6px;
  padding: 6px 8px;
}
.fund-summary { margin-bottom: 4px; }
.fund-row { display: flex; justify-content: space-between; padding: 2.5px 0; font-size: 11.5px; }
.fund-row.highlight { border-top: 1px solid #333355; border-bottom: 1px solid #333355; margin: 3px 0; padding: 5px 0; }
.flabel { color: #777; }
.fval { color: #c0c0c0; font-family: monospace; font-weight: 500; }
.fval.bold { font-weight: 700; }

.fund-breakdown { border-top: 1px dashed #333355; padding-top: 4px; }
.fb-item { display: flex; align-items: center; padding: 2px 0; font-size: 10.5px; gap: 4px; }
.fb-name { color: #666; width: 54px; flex-shrink: 0; }
.fb-bar-wrap {
  flex: 1; height: 14px; position: relative;
  background: rgba(255,255,255,0.04); border-radius: 2px;
  display: flex; overflow: hidden;
}
.fb-in { background: rgba(238,10,10,0.65); height: 100%; transition: width 0.3s; }
.fb-out { background: rgba(18,179,33,0.55); height: 100%; transition: width 0.3s; }
.fb-net { width: 60px; text-align: right; font-family: monospace; font-weight: 600; flex-shrink: 0; font-size: 10px; }

/* 控制面板通用 */
.period-panel, .indicator-panel, .sub-indicator-panel, .stock-input-panel {
  background: #1a1a2e;
  border-radius: 6px;
  padding: 8px 10px;
}
.panel-title {
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #2a2a4a;
}
.period-btns .el-button, .range-btns .el-button {
  margin: 2px !important;
  padding: 5px 8px !important;
}
.indicator-toggle, .sub-indicator-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.indicator-toggle >>> .el-checkbox,
.sub-indicator-toggle >>> .el-checkbox {
  margin-right: 0;
}
.indicator-toggle >>> .el-checkbox__label,
.sub-indicator-toggle >>> .el-checkbox__label {
  color: #aaa;
  font-size: 11px;
  padding-left: 4px;
}
.indicator-toggle >>> .el-checkbox__input.is-checked + .el-checkbox__label,
.sub-indicator-toggle >>> .el-checkbox__input.is-checked + .el-checkbox__label {
  color: #409EFF;
}

/* ===== 右侧图表区 ===== */
.chart-area {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.chart-area::-webkit-scrollbar { width: 6px; }
.chart-area::-webkit-scrollbar-thumb { background: #444; border-radius: 3px; }

.chart-main { width: 100%; height: 420px; }
.chart-vol { width: 100%; height: 120px; }
.chart-macd { width: 100%; height: 130px; }
.chart-kdj { width: 100%; height: 130px; }
.chart-rsi { width: 100%; height: 130px; }

.loading-mask {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(26, 26, 46, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #409EFF;
  font-size: 16px;
  z-index: 100;
  gap: 8px;
}
.empty-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #555;
  font-size: 14px;
}

/* ===== 右侧数据面板 ===== */
.right-sidebar {
  width: 260px;
  min-width: 260px;
  background: #16213e;
  border-left: 1px solid #2a2a4a;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.right-sidebar >>> .el-tabs__header { margin: 0; }
.right-sidebar >>> .el-tabs__nav { border-radius: 0; }
.right-sidebar >>> .el-tabs--card > .el-tabs__header .el-tabs__item {
  border: none;
  border-right: 1px solid #2a2a4a;
  background: #1a1a2e;
  color: #888;
  font-size: 11px;
  padding: 0 12px;
  line-height: 32px;
  height: 32px;
}
.right-sidebar >>> .el-tabs--card > .el-tabs__header .el-tabs__item.is-active {
  background: #16213e;
  color: #409EFF;
}
.right-sidebar >>> .el-tabs__content { padding: 0; height: calc(100vh - 145px); }
.right-panel-body {
  height: 100%;
  overflow-y: auto;
  padding: 6px;
}
.right-panel-body::-webkit-scrollbar { width: 3px; }
.right-panel-body::-webkit-scrollbar-thumb { background: #444; border-radius: 2px; }
.right-empty { padding: 20px 12px; text-align: center; color: #666; font-size: 11px; }

/* 右侧逐笔 */
.tick-summary-bar { color: #888; font-size: 9px; padding: 2px 4px 4px; border-bottom: 1px solid rgba(255,255,255,0.03); margin-bottom: 2px; }

/* 右侧竞价日期 */
.auction-date { color: #888; font-size: 9px; float: right; }

/* 右侧面板内的列表紧凑化 */
.right-panel-body .rt-minutes-list { max-height: none !important; }
</style>
